import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Trash2, CheckCircle2, AlertCircle, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OWNER_EMAIL = "styllos.cleber@gmail.com";

export default function DeleteAccountPublicPage() {
  const [form, setForm] = useState({ email: "", name: "", reason: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email || !form.email.includes("@")) {
      setError("Informe um e-mail válido.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/public/deletion-requests`, {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim() || null,
        reason: form.reason.trim() || null,
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Não foi possível registrar sua solicitação. Tente novamente ou envie um e-mail diretamente.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      <header className="sticky top-0 z-10 border-b border-white/5 bg-slate-950/80 backdrop-blur">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center gap-3">
          <Link to="/" data-testid="del-back-link" className="w-9 h-9 rounded-xl bg-slate-800/70 border border-white/5 flex items-center justify-center text-slate-300 hover:text-white">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-semibold">Põe na Conta</p>
            <h1 className="font-display text-lg sm:text-xl font-bold text-white truncate">Excluir minha conta</h1>
          </div>
        </div>
      </header>

      <main data-testid="delete-account-public-page" className="max-w-2xl mx-auto px-5 py-8 space-y-6">
        <div className="pnc-card p-6 space-y-3">
          <div className="w-12 h-12 rounded-2xl bg-rose-500/15 border border-rose-500/25 text-rose-300 flex items-center justify-center">
            <Trash2 className="w-5 h-5" />
          </div>
          <h2 className="font-display text-xl font-bold text-white">Como excluir sua conta e seus dados</h2>
          <p className="text-sm text-slate-300">
            O Põe na Conta oferece <strong>duas formas</strong> de exclusão de conta, previstas em nossa <Link to="/politica-de-privacidade" className="text-emerald-300 underline">Política de Privacidade</Link> e na LGPD.
          </p>
          <div className="space-y-2 text-sm text-slate-300">
            <p><strong className="text-white">1. Dentro do aplicativo (recomendado):</strong> faça login, abra <em>Perfil</em> e toque em <strong>“Excluir minha conta”</strong>. Após confirmar, sua conta e os lançamentos financeiros serão apagados imediatamente. Os registros de pagamento, quando existirem, são mantidos de forma <strong>anonimizada</strong> (desvinculados do seu identificador pessoal) apenas para cumprir obrigações fiscais.</p>
            <p><strong className="text-white">2. Pelo formulário abaixo:</strong> use esta opção se você não consegue mais acessar sua conta ou já desinstalou o aplicativo. A solicitação é registrada e processada manualmente após verificação da sua identidade.</p>
          </div>
          <p className="text-xs text-slate-400">
            O que será excluído: dados de cadastro (nome, e-mail, senha hash) e todos os lançamentos financeiros vinculados à conta. O que pode ser mantido anonimizado: registros de pagamento Stripe para fins fiscais, sem vínculo com sua identidade.
          </p>
        </div>

        {done ? (
          <div data-testid="deletion-success" className="pnc-card p-6 space-y-3 border-emerald-500/30">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 text-emerald-300 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <h3 className="font-display text-lg font-bold text-white">Solicitação recebida</h3>
            <p className="text-sm text-slate-300">Recebemos sua solicitação de exclusão. Ela será verificada e processada em breve. Se tiver qualquer dúvida, escreva para <a href={`mailto:${OWNER_EMAIL}`} className="text-emerald-300 underline">{OWNER_EMAIL}</a>.</p>
            <Link to="/" className="inline-block mt-2 px-4 py-2 rounded-xl bg-slate-800/70 border border-white/5 text-slate-200 hover:bg-slate-800 font-semibold text-sm">Voltar ao início</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="pnc-card p-6 space-y-4">
            <h3 className="font-display text-lg font-bold text-white">Formulário de solicitação</h3>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">E-mail da conta *</label>
              <input
                data-testid="deletion-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-slate-950/60 border border-white/10 text-white placeholder:text-slate-500 outline-none focus:border-emerald-500/50"
                placeholder="voce@exemplo.com"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Nome (opcional)</label>
              <input
                data-testid="deletion-name-input"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3 rounded-xl bg-slate-950/60 border border-white/10 text-white placeholder:text-slate-500 outline-none focus:border-emerald-500/50"
                placeholder="Como você se cadastrou"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Motivo (opcional)</label>
              <textarea
                data-testid="deletion-reason-input"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                rows={3}
                maxLength={1000}
                className="w-full px-4 py-3 rounded-xl bg-slate-950/60 border border-white/10 text-white placeholder:text-slate-500 outline-none focus:border-emerald-500/50 resize-none"
                placeholder="Nos ajuda a entender (não é obrigatório)"
              />
            </div>
            {error && (
              <div className="flex items-start gap-2 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/20 rounded-xl p-3">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <button
              type="submit"
              data-testid="deletion-submit-btn"
              disabled={sending}
              className="w-full py-3.5 rounded-2xl bg-rose-500 hover:bg-rose-600 disabled:opacity-60 text-white font-bold flex items-center justify-center gap-2 text-sm"
            >
              {sending ? "Enviando…" : (<><Trash2 className="w-4 h-4" /> Enviar solicitação de exclusão</>)}
            </button>
            <p className="text-xs text-slate-400 text-center">
              Prefere e-mail? Escreva para <a href={`mailto:${OWNER_EMAIL}`} className="text-emerald-300 underline">{OWNER_EMAIL}</a>.
            </p>
          </form>
        )}

        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Mail className="w-3.5 h-3.5" />
          <span>Responsável pelos dados: {OWNER_EMAIL}</span>
        </div>
      </main>
    </div>
  );
}
